<script setup lang="ts">
import { ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import yup from '@/plugins/yup'
import InputGroup from '@/components/Auths/InputGroup.vue'
import ButtonLoading from '@/components/Buttons/ButtonLoading.vue'
import { ValidationError } from 'yup'
import { useErrorStore } from '@/store/error'
import { InputFromField } from '@/interfaces/components/inputFromField'
import {
  formatOutsideRegularUtcTimeToLocalHHmm,
  localTimeInputToUtcHHmmssForApi,
} from '@/utils/date/date-time'
import { IOutsideRegularTimeAlarmSensor } from '@/interfaces/entities/alarm_profile'
import OutlineButton from '@/components/Globals/Buttons/OutlineButton.vue'
import { useAlarmProfileStore } from '@/store/alarmProfile'

const { t } = useI18n()
const profileStore = useAlarmProfileStore()
const isLoading = ref(false)

const emit = defineEmits(['cancel'])

const props = defineProps<{
  currentProfile: IOutsideRegularTimeAlarmSensor | null,
  formType: 'create' | 'edit',
}>()

const currentSettings = props.currentProfile?.settings

const inputs: Ref<InputFromField[]> = ref([
  {
    type: 'text',
    label: t('Nome'),
    placeholder: 'alarms.inform_profile_name',
    value: props.formType==='edit' ? props.currentProfile?.name ?? '' : '',
  },
  {
    type: 'text',
    label: t('alarms.outside_regular_time.setpoint'),
    placeholder: 'alarms.current_message',
    value: props.formType==='edit' ? currentSettings?.outside_regular_time_setpoint ?? '' : '',
  },
  {
    type: 'text',
    label: t('alarms.outside_regular_time.duration_setpoint'),
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType==='edit' ? currentSettings?.outside_regular_time_duration_setpoint ?? 60 : 60,
  },
  {
    type: 'text',
    label: t('alarms.outside_regular_time.debounce_duration_setpoint'),
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType==='edit' ? currentSettings?.outside_regular_time_debounce_duration_setpoint ?? 5 : 5,
  },
  {
    label: t('alarms.outside_regular_time.start_time'),
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType==='edit' ? currentSettings?.outside_regular_time_start_time ? formatOutsideRegularUtcTimeToLocalHHmm(currentSettings.outside_regular_time_start_time) : '12:00' : '12:00',
    type: 'time',
  },
  {
    label: t('alarms.outside_regular_time.end_time'),
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType==='edit' ? currentSettings?.outside_regular_time_end_time ? formatOutsideRegularUtcTimeToLocalHHmm(currentSettings.outside_regular_time_end_time) : '12:00' : '12:00',
    type: 'time',
  },

])

const alarmSettingsSchema = yup.object({
  name: yup
    .string()
    .required('O campo nome é obrigatório'),
  outside_regular_time_setpoint: yup
    .number()
    .typeError('O campo de duração de carga baixa deve ser um número')
    .required('O campo de duração de carga baixa é obrigatório'),

  outside_regular_time_duration_setpoint: yup
    .number()
    .typeError('O campo de corrente máxima deve ser um número')
    .required('O campo de corrente máxima é obrigatório')
    .min(0, 'A corrente máxima não pode ser menor que zero'),

  outside_regular_time_debounce_duration_setpoint: yup
    .number()
    .typeError('O campo de corrente mínima deve ser um número')
    .required('O campo de corrente mínima é obrigatório')
    .min(0, 'A corrente mínima não pode ser menor que zero'),
  outside_regular_time_start_time: yup
    .string()
    .required('O campo de tempo de inicio é obrigatório')
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?$/, 'Formato deve ser HH:mm'),
  outside_regular_time_end_time: yup
    .string()
    .required('O campo de tempo de fim é obrigatório')
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?$/, 'Formato deve ser HH:mm')
})

async function handleSubmit() {
  const values: IOutsideRegularTimeAlarmSensor = {
    name: String(inputs.value[0].value),
    outside_regular_time_setpoint: Number(inputs.value[1].value),
    outside_regular_time_duration_setpoint: Number(inputs.value[2].value),
    outside_regular_time_debounce_duration_setpoint: Number(inputs.value[3].value),
    outside_regular_time_start_time: localTimeInputToUtcHHmmssForApi(String(inputs.value[4].value)),
    outside_regular_time_end_time: localTimeInputToUtcHHmmssForApi(String(inputs.value[5].value)),
}
try {
    await alarmSettingsSchema.validate(values, { abortEarly: false })
    isLoading.value = true
    if(props.formType === 'create'){
      await profileStore.createOutsideRegularTimeAlarmProfile(values)
    } else if(props.formType === 'edit' && props.currentProfile){
      await profileStore.editOutsideRegularTimeAlarmProfile(props.currentProfile.id, values)
    }
    isLoading.value = false
    emit('cancel')
  } catch (err) {
    if (err instanceof ValidationError) {
      useErrorStore().clearErrors();
      useErrorStore().setErrors('Erro ao criar perfil: ', err.errors)
    }
  }
}
</script>

<template>
  <div class="border border-stroke bg-gray-2 p-5 shadow-sm">
    <div class="flex flex-wrap gap-4">
      <span v-if="props.formType ==='create'" class="w-full text-base font-medium text-gray-500 mb-1">
        {{ t('alarms.outside_regular_time.create_profile') }}
      </span>
      <div v-if="props.formType === 'edit'" class="w-full flex justify-items-center">
        <span class="w-full text-base font-medium text-gray-500 mb-1">
          {{ "Editar perfil: " + (props.currentProfile?.name || '') }}
        </span>
      </div>
      <div class="w-full border-t border-stroke pt-1"></div>
      <template v-for="(input, index) in inputs" :key="index">
        <div class="min-w-[200px] w-full">
          <InputGroup
            v-model="input.value"
            :type="input.type ?? 'text'"
            :label="t(input.label)"
            :placeholder="t(input.placeholder)"
          />
        </div>
      </template>

      <div class="flex w-full justify-end gap-2">
        <OutlineButton 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !border-stroke !bg-white !shadow-sm hover:!bg-gray-2 !flex !items-center !justify-center whitespace-nowrap"
          @click="emit('cancel')"
          label="Cancelar" />
        <ButtonLoading 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !text-white !border-blue-600 !bg-blue-600 !shadow-sm hover:!bg-blue-700 !flex !items-center !justify-center whitespace-nowrap"
          :isLoading="isLoading"
          @submit="handleSubmit"
          label="Salvar" />
      </div>
    </div>
  </div>
</template>

<style scoped>
  :deep(label) {
    color: #64748b;
    font-size: 0.875rem;
  }
  :deep(input) {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
</style>
